import React, { useEffect } from 'react';

// Hooks
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Utils
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';
import { dateFormat } from '~/utils/date-format';
import { stringLength } from '~/utils/helperFunctions';

// Components
import OlcTablePagination from '~/components/General/OlcTablePagination';

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import { Box, Typography, Stack, Tooltip } from '@mui/material';

// Icons
import { NoContactCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const Table = (props) => {
  const { pagination, updatePagination, searchApplied } = props;
  const pageSizes = DEFAULT_PAGE_SIZES;
  const rows = useSelector((state) => state.adminLookupsReducers.rows) || [];

  const to = useSelector((state) => state.adminLookupsReducers.to);
  const from = useSelector((state) => state.adminLookupsReducers.from);
  const lastPage = useSelector((state) => state.adminLookupsReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.adminLookupsReducers.currentPage || 1);
  const rowsCount = useSelector((state) => state.adminLookupsReducers.rowsCount) || 0;

  const perPage = useSelector(
    (state) => state.adminLookupsReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const isLoading = useSelector((state) => state.adminLookupsReducers.loading);

  const handleChangePage = (event, newPage) => {
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const columns = [
    {
      field: 'batchId',
      headerName: 'Batch ID',
      flex: 1,
      minWidth: 130,
      sortable: false,
      renderCell: (params) =>
        params.row.order?.batchId ? (
          <Box className="addressWrapper">
            <Link
              className="link"
              to={`/admin/batches/${params.row.order.batchId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Typography>{params.row.order.batchId}</Typography>
            </Link>
          </Box>
        ) : (
          <Box className="addressWrapper">
            <Typography>{' -'}</Typography>
          </Box>
        ),
    },
    {
      field: 'mailedDate',
      headerName: 'Mailed Date',
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>
            {params.row.order?.mailedDate ? dateFormat(params.row.order.mailedDate, true, true) : ' -'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'orderItemId',
      headerName: 'Order Item ID',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.id}</Typography>
        </Box>
      ),
    },
    {
      field: 'barcode',
      headerName: 'Barcode',
      flex: 2,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => {
        const barcode = params.row.meta?.barcode || ' -';
        const barcodeContent = (
          <Box className="addressWrapper">
            <Typography>{barcode}</Typography>
          </Box>
        );

        return stringLength(`${barcode}`, 30) ? (
          <Tooltip title={barcode}>{barcodeContent}</Tooltip>
        ) : (
          barcodeContent
        );
      },
    },
  ];

  useEffect(() => {
    if (rows.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [rows]);

  return (
    <div className="adminLookupsWrapper olcAdminListTable">
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight={false}
        loading={isLoading}
        pageSizeOptions={[20, 50, 100]}
        rowCount={rowsCount || 0}
        pagination={pagination}
        paginationMode="server"
        className="adminLookupsGrid"
        hideFooterSelectedRowCount
        hideFooterPagination
        rowSelection={false}
        getRowId={(row) => row?.id}
        disableColumnMenu={true}
        columnHeaderHeight={47}
        slots={{
          noRowsOverlay: () => (
            <Stack className="noRowsTextContacts">
              <img src={NoContactCIcon} alt="NoContact" />
              {`${MESSAGES.NO_RESULTS_TEXT} ${!searchApplied ? 'filters' : 'search'}`}
            </Stack>
          ),
          noResultsOverlay: () => (
            <Stack className="noRowsTextContacts">
              <img src={NoContactCIcon} alt="NoContact" />
              {MESSAGES.NO_ROWS_TEXT}
            </Stack>
          ),
        }}
      />
      {(isLoading || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={(event) => updatePagination({ page: 0, pageSize: event.target.value })}
          from={from}
          to={rowsCount < to ? rows?.length + from - 1 : to}
          total={rowsCount}
          loading={isLoading}
        />
      )}
    </div>
  );
};

export default Table;
